import { useMemo } from "react";

import { API_ENDPOINTS } from "../../api/constants";
import useFetch from "../../api/useFetch";

/*
 * Using a company, provide a useful object of booleans that indicate the level of access
 * the user has to the company's data.
 *
 * `hasAnyAccess` is true only when the user has at least one `can_*` permission, matching
 * HD. It gates the whole profile; the company's own `show_*_data` flags say nothing about
 * the user and must not open it (ZX-15335).
 */
const useCompanyAccess = company => {
  const companyId = company?.zb_id;
  const hasProDataAccessQueryInfo = useFetch(
    API_ENDPOINTS.HAS_PRO_DATA_ACCESS(companyId),
    {},
    {
      retryOnMount: false,
      enabled: Boolean(companyId),
    }
  );

  return useMemo(() => {
    if (
      hasProDataAccessQueryInfo.isLoading ||
      hasProDataAccessQueryInfo.isError ||
      !hasProDataAccessQueryInfo.cleanedData
    ) {
      return {
        canDownloadPDFReport: false,
        canDownloadZXData: false,
        canViewBidAskPriceGraph: false,
        canViewBidAskRatioGraph: false,
        canViewPriceAndVolumeGraph: false,
        canViewRelativeActivityGraph: false,
        canViewReportedMarksGraph: false,
        canViewSacraDocuments: false,
        canViewClosedTransactions: false,
        isLoading: hasProDataAccessQueryInfo.isLoading,
        isError: hasProDataAccessQueryInfo.isError,
        hasAnyAccess: false,
        timeFrame: null,
      };
    }
    const hasAnyCanFlag = Object.keys(hasProDataAccessQueryInfo.cleanedData)
      .filter(key => key.startsWith("can_"))
      .some(key => hasProDataAccessQueryInfo.cleanedData[key]);
    return {
      canDownloadPDFReport: hasProDataAccessQueryInfo.cleanedData.can_download_pdf_report,
      canDownloadZXData: hasProDataAccessQueryInfo.cleanedData.can_download_zx_data,
      canViewBidAskPriceGraph: hasProDataAccessQueryInfo.cleanedData.can_view_bid_ask_price_graph,
      canViewBidAskRatioGraph: hasProDataAccessQueryInfo.cleanedData.can_view_bid_ask_ratio_graph,
      canViewPriceAndVolumeGraph:
        hasProDataAccessQueryInfo.cleanedData.can_view_price_and_volume_graph,
      canViewRelativeActivityGraph:
        hasProDataAccessQueryInfo.cleanedData.can_view_relative_activity_graph,
      canViewReportedMarksGraph:
        hasProDataAccessQueryInfo.cleanedData.can_view_reported_marks_graph,
      canViewSacraDocuments: hasProDataAccessQueryInfo.cleanedData.can_view_sacra_documents,
      canViewClosedTransactions: hasProDataAccessQueryInfo.cleanedData.can_view_closed_transactions,
      isLoading: hasProDataAccessQueryInfo.isLoading,
      isError: hasProDataAccessQueryInfo.isError,
      hasAnyAccess: hasAnyCanFlag,
      timeFrame: hasProDataAccessQueryInfo.cleanedData.time_frame,
    };
  }, [hasProDataAccessQueryInfo]);
};

export default useCompanyAccess;
